import { DiamondIcon } from './DiamondIcon.js';
import { ShoppingBag, UserPlus, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isGameRoute, openGameTopUp } from '../game-top-up.js';
import { trackAnalytics } from '../analytics.js';
import { api } from '../api.js';
import {
  DIAMOND_SHORTAGE_EVENT,
  DIAMOND_SHORTAGE_CLOSE_EVENT,
  type DiamondShortageDetail,
} from '../diamond-shortage.js';
import { rememberGameInviteReturn } from '../game-invite-return.js';
import { playGameHaptic } from '../haptics.js';
import { platformShareUrl } from '../share-links.js';
import { useAppStore } from '../store.js';

export function DiamondShortageDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();
  const inGame = isGameRoute(location.pathname);
  const adapter = useAppStore((state) => state.adapter);
  const haptics = useAppStore((state) => state.haptics);
  const session = useAppStore((state) => state.session);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [detail, setDetail] = useState<DiamondShortageDetail | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [referralUrl, setReferralUrl] = useState<string | null>(null);

  useEffect(() => {
    const open = (event: Event): void => {
      const customEvent = event as CustomEvent<DiamondShortageDetail>;
      setDetail(customEvent.detail ?? {});
      setStatus(null);
      setReferralUrl(null);
      trackAnalytics('insufficient_gems_shown', {
        source: customEvent.detail?.source ?? 'api',
      });
      window.requestAnimationFrame(() => closeButton.current?.focus());
    };
    window.addEventListener(DIAMOND_SHORTAGE_EVENT, open);
    const close = () => setDetail(null);
    window.addEventListener(DIAMOND_SHORTAGE_CLOSE_EVENT, close);
    return () => {
      window.removeEventListener(DIAMOND_SHORTAGE_EVENT, open);
      window.removeEventListener(DIAMOND_SHORTAGE_CLOSE_EVENT, close);
    };
  }, []);

  useEffect(() => {
    if (!detail || !referralUrl || !inGame) return;
    const refresh = () => {
      if (document.visibilityState === 'visible')
        void client.invalidateQueries({ queryKey: ['home'] });
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const timer = window.setInterval(refresh, 10_000);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [client, detail, inGame, referralUrl]);

  useEffect(() => {
    if (!detail) return;
    const close = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setDetail(null);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [detail]);

  if (!detail) return null;

  const invite = async (): Promise<void> => {
    if (!session || !adapter) return;
    setSharing(true);
    setStatus(null);
    trackAnalytics('earn_gems_clicked', { source: 'diamond_shortage_referral' });
    let preparedUrl: string | null = null;
    try {
      if (adapter.platform === 'max' && !inGame) {
        const card = await api.maxReferralCard();
        await adapter.openExternal(card.botUrl);
        setStatus('Личная ссылка готова в боте — отправьте её другу.');
      } else {
        const userId = session.user.internalId;
        if (!userId) throw new Error('Профиль ещё загружается. Попробуйте снова.');
        const url = await platformShareUrl(adapter, `ref_${userId}`);
        preparedUrl = url;
        setReferralUrl(url);
        if (inGame) rememberGameInviteReturn(userId, location.pathname + location.search);
        const result = await adapter.share({
          text: 'Сыграй со мной в БлокБитву — после твоего первого входа мы оба получим по 20 монет пути!',
          url,
          mode: 'default',
          preserveContext: inGame,
        });
        setStatus(
          result.method === 'clipboard'
            ? 'Ссылка скопирована — отправьте её другу.'
            : 'Приглашение готово — выберите друга.',
        );
      }
      playGameHaptic(adapter, haptics, 'reward');
    } catch (error) {
      setStatus(
        error instanceof Error && error.name === 'AbortError'
          ? 'Приглашение отменено. Можно вернуться к игре.'
          : preparedUrl
            ? 'Не удалось открыть отправку. Скопируйте личную ссылку ниже и отправьте другу.'
            : 'Не удалось получить приглашение. Попробуйте ещё раз.',
      );
      playGameHaptic(adapter, haptics, 'error');
    } finally {
      setSharing(false);
      if (inGame) void client.invalidateQueries({ queryKey: ['home'] });
    }
  };

  return (
    <div className="modal-backdrop diamond-shortage-backdrop" onMouseDown={() => setDetail(null)}>
      <section
        className="app-modal diamond-shortage-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="diamond-shortage-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-header">
          <div className="diamond-shortage-title">
            <DiamondIcon aria-hidden="true" />
            <div>
              <span className="eyebrow">Недостаточно для покупки</span>
              <h2 id="diamond-shortage-title">Не хватает алмазов</h2>
            </div>
          </div>
          <button
            ref={closeButton}
            className="icon-button"
            type="button"
            aria-label="Закрыть"
            onClick={() => setDetail(null)}
          >
            <X aria-hidden="true" />
          </button>
        </header>
        <p>
          Алмазы доступны только за деньги. За приглашение нового друга вы оба получите по 20 монет
          пути — ими нельзя оплатить покупку за алмазы.
        </p>
        <div className="diamond-shortage-actions">
          <button
            className="primary-button"
            type="button"
            onClick={() => {
              trackAnalytics('gem_store_opened', { source: 'diamond_shortage' });
              setDetail(null);
              if (isGameRoute(location.pathname)) openGameTopUp();
              else void navigate('/store?view=gems&source=diamond_shortage');
            }}
          >
            <ShoppingBag aria-hidden="true" /> Купить алмазы
          </button>
          <button
            className="secondary-button"
            type="button"
            disabled={sharing}
            onClick={() => void invite()}
          >
            <UserPlus aria-hidden="true" /> {sharing ? 'Открываем…' : 'Позвать друга'}
          </button>
        </div>
        {inGame ? (
          <p>
            Партия остаётся открытой. Приглашение само по себе не добавляет ходы: награда придёт
            после первого входа нового друга.
            {detail.source === 'ranked_rescue'
              ? ' Новый набор стоит 50 алмазов и покупается отдельно.'
              : ''}
          </p>
        ) : null}
        {referralUrl ? (
          <div className="game-referral-link">
            <label>
              Ваша ссылка для друга
              <input
                aria-label="Ваша ссылка для друга"
                readOnly
                value={referralUrl}
                onFocus={(event) => event.currentTarget.select()}
              />
            </label>
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                void (async () => {
                  try {
                    await navigator.clipboard.writeText(referralUrl);
                    setStatus('Ссылка скопирована — отправьте её другу.');
                  } catch {
                    setStatus('Выделите ссылку и скопируйте её вручную.');
                  }
                })();
              }}
            >
              Скопировать ссылку
            </button>
          </div>
        ) : null}
        {inGame ? (
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              void client.invalidateQueries({ queryKey: ['home'] });
              setDetail(null);
            }}
          >
            Вернуться к игре
          </button>
        ) : null}
        {status ? (
          <p className="diamond-shortage-status" role="status">
            {status}
          </p>
        ) : null}
      </section>
    </div>
  );
}
